import { Suspense, lazy, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BarChart3Icon, Loader2Icon, LockKeyholeIcon, MonitorCogIcon, SlidersHorizontalIcon, UserRoundIcon } from 'lucide-react';
import { authClient } from '@/lib/auth-client';
import { AccountPasswordForm } from '@/components/account-password-form';
import { AccountProfileForm } from '@/components/account-profile-form';
import { AccountSessionsPanel } from '@/components/account-sessions-panel';
import { useOrganizationAccess } from '@/features/organization/useOrganizationAccess';
import { cn } from '@/lib/utils';
import type { SettingsTab } from '@/lib/settings-tabs';
import { resolveSettingsTab, SETTINGS_TAB_PARAM } from '@/lib/settings-tabs';

const AssistantSettingsPanel = lazy(() => import('@/features/assistant-settings').then(
  (module) => ({ default: module.AssistantSettingsPanel }),
));

const UsagePage = lazy(() => import('@/features/usage/components/UsagePage').then(
  (module) => ({ default: module.UsagePage }),
));

function PanelLoader() {
  return (
    <div className="flex min-h-40 items-center justify-center" role="status" aria-label="Chargement">
      <Loader2Icon className="size-6 animate-spin text-muted-foreground" />
    </div>
  );
}

const NAV_ITEM_CLASS = 'flex h-10 w-full items-center justify-start gap-2.5 rounded-2xl border border-transparent bg-transparent px-3 text-[15px] font-medium text-foreground/80 transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1 focus-visible:outline-ring dark:text-muted-foreground dark:hover:text-sidebar-accent-foreground [&_svg]:size-5 [&_svg]:shrink-0';
const NAV_ITEM_ACTIVE_CLASS = 'bg-sidebar-accent text-sidebar-accent-foreground dark:text-sidebar-accent-foreground';


export function SettingsPage() {
  const sessionState = authClient.useSession();
  const session = sessionState.data;
  const orgAccess = useOrganizationAccess();
  const [searchParams, setSearchParams] = useSearchParams();
  const [refreshSignal, setRefreshSignal] = useState(0);

  // Le Général est identique pour tous : logo + personnalité de l'entreprise
  // active. L'admin plateforme pilote le global et les autres entreprises
  // depuis Configuration technique (onglet Assistant), pas depuis ici.
  const canSeeGeneralTab = orgAccess.canReadAssistantSettings;
  const activeTab = resolveSettingsTab({
    canSeeGeneralTab,
    requested: searchParams.get(SETTINGS_TAB_PARAM),
  });

  if (!session) {
    return null;
  }

  // Les droits d'entreprise arrivent de façon asynchrone : tant qu'ils sont
  // inconnus, `canSeeGeneralTab` vaudrait `false` et un `?tab=general` légitime
  // retomberait sur Profil avant de sauter sur Général. On attend donc la
  // résolution.
  if (orgAccess.isPending) {
    return (
      <section className="w-full">
        <PanelLoader />
      </section>
    );
  }

  async function handleAccountUpdated() {
    await authClient.getSession({
      query: { disableCookieCache: true },
    });
    await sessionState.refetch();
    setRefreshSignal((current) => current + 1);
  }

  function selectTab(nextTab: SettingsTab) {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set(SETTINGS_TAB_PARAM, nextTab);
    setSearchParams(nextParams, { replace: true });
  }

  const navItems: { icon: typeof UserRoundIcon; label: string; tab: SettingsTab }[] = [
    ...(canSeeGeneralTab ? [{ icon: SlidersHorizontalIcon, label: 'Général', tab: 'general' as const }] : []),
    { icon: BarChart3Icon, label: 'Usage', tab: 'usage' },
    { icon: UserRoundIcon, label: 'Profil', tab: 'profile' },
    { icon: LockKeyholeIcon, label: 'Sécurité', tab: 'security' },
    { icon: MonitorCogIcon, label: 'Sessions', tab: 'sessions' },
  ];

  return (
    <section className="flex w-full flex-col xl:flex-row xl:gap-10">
      <aside className="xl:w-[17rem]">
        <nav aria-label="Paramètres" className="xl:sticky xl:top-0">
          <ul className="flex w-full flex-col gap-1">
            {navItems.map((item) => (
              <li key={item.tab}>
                <button
                  type="button"
                  aria-current={activeTab === item.tab ? 'page' : undefined}
                  data-testid={`settings-tab-${item.tab}`}
                  onClick={() => selectTab(item.tab)}
                  className={cn(NAV_ITEM_CLASS, activeTab === item.tab && NAV_ITEM_ACTIVE_CLASS)}
                >
                  <item.icon />
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </nav>
      </aside>

      <div className="min-w-0 flex-1">
        {activeTab === 'general' && canSeeGeneralTab ? (
          <Suspense fallback={<PanelLoader />}>
            <AssistantSettingsPanel />
          </Suspense>
        ) : null}

        {activeTab === 'usage' ? (
          <Suspense fallback={<PanelLoader />}>
            <UsagePage />
          </Suspense>
        ) : null}

        {activeTab === 'profile' ? (
          <AccountProfileForm
            session={session}
            onAccountUpdated={handleAccountUpdated}
            className="rounded-none border-0 bg-transparent p-0 shadow-none"
          />
        ) : null}

        {activeTab === 'security' ? (
          <AccountPasswordForm
            onAccountUpdated={handleAccountUpdated}
            className="rounded-none border-0 bg-transparent p-0 shadow-none"
          />
        ) : null}

        {activeTab === 'sessions' ? (
          <AccountSessionsPanel
            currentSessionId={session.session.id}
            refreshSignal={refreshSignal}
            className="rounded-none border-0 bg-transparent p-0 shadow-none"
          />
        ) : null}
      </div>
    </section>
  );
}
